import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

function applyMonoClasses(enabled, color) {
  const html = document.documentElement;
  const MONO_COLORS = ['slate', 'stone', 'blue', 'teal', 'violet', 'rose'];

  MONO_COLORS.forEach(c => html.classList.remove(`mono-${c}`));

  if (enabled) {
    html.classList.add('mono');
    if (color && color !== 'slate') {
      html.classList.add(`mono-${color}`);
    }
  } else {
    html.classList.remove('mono');
  }
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState('light');
  const [monoTheme, setMonoThemeState] = useState(false);
  const [monoThemeColor, setMonoThemeColorState] = useState('slate');
  const [easterEggsEnabled, setEasterEggsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserSettings();
  }, []);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    applyMonoClasses(monoTheme, monoThemeColor);
  }, [monoTheme, monoThemeColor]);

  const loadUserSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setThemeState('light');
        setMonoThemeState(false);
        setMonoThemeColorState('slate');
        setEasterEggsEnabled(false);
        setLoading(false);
        return;
      }

      const settings = await apiService.getUserSettings();
      setThemeState(settings.theme || 'light');
      setMonoThemeState(settings.mono_theme || false);
      setMonoThemeColorState(settings.mono_theme_color || 'slate');
      setEasterEggsEnabled(settings.easter_eggs_enabled !== undefined ? settings.easter_eggs_enabled : false);
    } catch (error) {
      console.error('Failed to load user settings:', error);
      setThemeState('light');
      setMonoThemeState(false);
      setMonoThemeColorState('slate');
      setEasterEggsEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setThemeState(newTheme);
    try {
      await apiService.updateUserSettings({ theme: newTheme });
    } catch (error) {
      console.error('Failed to update theme:', error);
      setThemeState(theme);
    }
  };

  const setThemeValue = async (newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') return;
    setThemeState(newTheme);
    try {
      await apiService.updateUserSettings({ theme: newTheme });
    } catch (error) {
      console.error('Failed to update theme:', error);
      setThemeState(theme);
    }
  };

  const setMonoTheme = async (enabled) => {
    setMonoThemeState(enabled);
    try {
      await apiService.updateUserSettings({ mono_theme: enabled });
    } catch (error) {
      console.error('Failed to update mono theme:', error);
      setMonoThemeState(monoTheme);
    }
  };

  const setMonoThemeColor = async (color) => {
    setMonoThemeColorState(color);
    try {
      await apiService.updateUserSettings({ mono_theme_color: color });
    } catch (error) {
      console.error('Failed to update mono theme color:', error);
      setMonoThemeColorState(monoThemeColor);
    }
  };

  const toggleEasterEggs = async () => {
    const newValue = !easterEggsEnabled;
    setEasterEggsEnabled(newValue);
    try {
      await apiService.updateUserSettings({ easter_eggs_enabled: newValue });
    } catch (error) {
      console.error('Failed to update easter eggs setting:', error);
      setEasterEggsEnabled(easterEggsEnabled);
    }
  };

  const setEasterEggsValue = async (enabled) => {
    setEasterEggsEnabled(enabled);
    try {
      await apiService.updateUserSettings({ easter_eggs_enabled: enabled });
    } catch (error) {
      console.error('Failed to update easter eggs setting:', error);
      setEasterEggsEnabled(easterEggsEnabled);
    }
  };

  const value = {
    theme,
    monoTheme,
    monoThemeColor,
    easterEggsEnabled,
    loading,
    toggleTheme,
    setTheme: setThemeValue,
    setMonoTheme,
    setMonoThemeColor,
    toggleEasterEggs,
    setEasterEggsEnabled: setEasterEggsValue,
    refreshSettings: loadUserSettings,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
